import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Select from "react-select";
import DatePicker from "react-multi-date-picker";
import { MONTHS_ENUM } from "../../../common/Constants";
import {
    useReplicateAuditSchedule,
    useReplicateVendorAuditSchedule,
    useGetCompanies,
    useGetCompanyLocations,
    useGetVendorCategories,
    useGetCompanyVendorLocations,
} from "../../../../backend/masters";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { ERROR_MESSAGES } from "../../../../utils/constants";
import { getMaxMonthYear } from "../../../../utils/common";
import { DEFAULT_OPTIONS_PAYLOAD } from "../../../common/Table";

interface Props {
    companyId: string;
    companyLabel: string;
    associateCompanyId: string;
    associateCompanyLabel: string;
    locationId: string;
    locationLabel: string;
    fromMonth: string;
    fromYear: number;
    selectedIds: string[];
    selectedCount: number;
    totalCount: number;
    onClose: () => void;
    onSuccess: () => void;
    isVendor?: boolean;
    vendorCategoryId?: string;
    vendorCategoryLabel?: string;
    vendorRegistrationId?: string;
    vendorLabel?: string;
}

const DRAWER_MIN_DATE = (() => {
    const date = new Date();
    date.setDate(1);
    return date;
})();

const DRAWER_MAX_DATE = getMaxMonthYear();

function AuditReplicationDrawer({
    companyId, companyLabel,
    associateCompanyId, associateCompanyLabel,
    locationId, locationLabel,
    fromMonth, fromYear,
    selectedIds, selectedCount, totalCount,
    onClose, onSuccess,
    isVendor,
    vendorCategoryId, vendorCategoryLabel,
    vendorRegistrationId, vendorLabel,
}: Props) {
    const [toMonths, setToMonths] = useState<any[]>([]);
    const [dryRunResult, setDryRunResult] = useState<any>(null);
    const [isDryRunning, setIsDryRunning] = useState(false);

    const fromMonthIndex = MONTHS_ENUM.indexOf(fromMonth);
    const toMonthsMinDate = (fromMonthIndex >= 0 && fromYear)
        ? new Date(fromYear, fromMonthIndex, 1)
        : DRAWER_MIN_DATE;

    // Destination selectors
    const [destCompany, setDestCompany] = useState<any>(null);
    const [destAssocCompany, setDestAssocCompany] = useState<any>(null);
    const [destLocation, setDestLocation] = useState<any>(null);
    const [destVendorCategory, setDestVendorCategory] = useState<any>(null);
    const [destVendorRegistration, setDestVendorRegistration] = useState<any>(null);

    const destinationFields = isVendor
        ? [destCompany, destAssocCompany, destLocation, destVendorCategory, destVendorRegistration]
        : [destCompany, destAssocCompany, destLocation];
    const anyDestinationSelected = destinationFields.some(Boolean);
    const allDestinationSelected = destinationFields.every(Boolean);
    const destinationIncomplete = anyDestinationSelected && !allDestinationSelected;

    const { companies: parentCompanies } = useGetCompanies({
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: { is_parent_eq: true, is_active_eq: true }
    }, true, 'destination');
    const { companies: assocCompanies } = useGetCompanies({
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: {
            is_parent_eq: 'false',
            is_active_eq: true,
            parent_company_id_eq: (destCompany || {}).value
        }
    }, Boolean(destCompany), 'destination');
    const { locations } = useGetCompanyLocations({
        ...DEFAULT_OPTIONS_PAYLOAD,
        includes: ['location'],
        include: ['location'],
        filters: { company_id_eq: (destAssocCompany || {}).value }
    }, Boolean((destAssocCompany || {}).value), 'destination');

    const { vendorCategories } = useGetVendorCategories({
        ...DEFAULT_OPTIONS_PAYLOAD
    }, 'destination');

    const destAssocId = destAssocCompany ? destAssocCompany.value : associateCompanyId;
    const destLocId = destLocation ? destLocation.value : locationId;
    const { vendorLocations } = useGetCompanyVendorLocations({
        filters: {
            associate_company_id_eq: destAssocId,
            location_id_eq: destLocId,
            vendor_category_id_eq: destVendorCategory ? destVendorCategory.value : '',
        },
        include: ['vendor_registration'],
        includes: ['vendor_registration'],
    }, Boolean(isVendor && destVendorCategory), 'destination');

    useEffect(() => {
        setDestAssocCompany(null);
        setDestLocation(null);
        setDestVendorCategory(null);
        setDestVendorRegistration(null);
    }, [destCompany]);

    useEffect(() => {
        setDestLocation(null);
        setDestVendorCategory(null);
        setDestVendorRegistration(null);
    }, [destAssocCompany]);

    useEffect(() => {
        setDestVendorCategory(null);
        setDestVendorRegistration(null);
    }, [destLocation]);

    useEffect(() => {
        setDestVendorRegistration(null);
    }, [destVendorCategory]);

    const { replicateAuditSchedule, replicating } = useReplicateAuditSchedule((response: any) => {
        if (response instanceof AxiosError) {
            const error = ((response.response || {}).data || {}).error || ERROR_MESSAGES.DEFAULT;
            toast.error(error);
            setIsDryRunning(false);
        } else {
            const data = (response || {}).data || {};
            if (data.dry_run) {
                setDryRunResult(data);
                setIsDryRunning(false);
            } else {
                toast.success("Audit Replication Completed");
                onSuccess();
                onClose();
            }
        }
    });

    const { replicateVendorAuditSchedule, replicating: replicatingVendor } = useReplicateVendorAuditSchedule((response: any) => {
        if (response instanceof AxiosError) {
            const error = ((response.response || {}).data || {}).error || ERROR_MESSAGES.DEFAULT;
            toast.error(error);
            setIsDryRunning(false);
        } else {
            const data = (response || {}).data || {};
            if (data.dry_run) {
                setDryRunResult(data);
                setIsDryRunning(false);
            } else {
                toast.success("Audit Replication Completed");
                onSuccess();
                onClose();
            }
        }
    });

    const isSubmitting = replicating || replicatingVendor;


    function dateObjectsToPayload(dates: any[]) {
        return dates.map((d: any) => {
            const date = d.toDate ? d.toDate() : new Date(d);
            return { month: MONTHS_ENUM[date.getMonth()], year: date.getFullYear() };
        });
    }

    function buildPayload(dryRun: boolean) {
        const base: any = {
            company_id: companyId,
            associate_company_id: associateCompanyId,
            location_id: locationId,
            from_month: fromMonth,
            from_year: fromYear,
            dry_run: dryRun,
            to_months: dateObjectsToPayload(toMonths)
        };
        if (selectedIds.length > 0) {
            base.todo_ids = selectedIds;
        }
        if (destCompany) base.to_company_id = destCompany.value;
        if (destAssocCompany) base.to_associate_company_id = destAssocCompany.value;
        if (destLocation) base.to_location_id = destLocation.value;
        if (isVendor) {
            base.vendor_category_id = vendorCategoryId;
            base.vendor_registration_id = vendorRegistrationId;
            if (destVendorCategory) base.to_vendor_category_id = destVendorCategory.value;
            if (destVendorRegistration) base.to_vendor_registration_id = destVendorRegistration.value;
        }
        return base;
    }

    useEffect(() => {
        if (toMonths.length > 0 && !destinationIncomplete) {
            setIsDryRunning(true);
            const payload = buildPayload(true);
            if (isVendor) {
                replicateVendorAuditSchedule(payload);
            } else {
                replicateAuditSchedule(payload);
            }
        } else {
            setDryRunResult(null);
        }
    }, [toMonths, destinationIncomplete]);

    function handleSubmit() {
        const payload = buildPayload(false);
        if (isVendor) {
            replicateVendorAuditSchedule(payload);
        } else {
            replicateAuditSchedule(payload);
        }
    }

    const displayCount = dryRunResult ? dryRunResult.selected_count : selectedCount;
    const displayTotal = dryRunResult ? dryRunResult.total_count : totalCount;

    const allMonthsBlocked = !!(
        dryRunResult &&
        dryRunResult.to_months &&
        dryRunResult.to_months.length > 0 &&
        dryRunResult.to_months.every((m: any) => m.blocked)
    );

    const vendorCategoryOptions = (vendorCategories || []).map((x: any) => ({ value: x.id, label: x.name || x.vendorCategoryName }));
    const vendorRegistrationOptions = (vendorLocations || []).map((x: any) => ({
        value: (x.vendor_registration || {}).id,
        label: (x.vendor_registration || {}).vendor_name,
    }));

    return (
        <Modal show={true} backdrop="static" animation={false} dialogClassName="drawer" size="lg">
            <Modal.Header closeButton={true} onHide={onClose}>
                <Modal.Title className="bg">Audit Replication</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex justify-content-start">
                    <div className="col-12">

                        {/* Source (From) — read-only */}
                        <div className="row mt-3">
                            <div className="col-12 px-3 mb-1">
                                <small className="text-muted fw-semibold text-uppercase" style={{ letterSpacing: '0.05em' }}>Source</small>
                                <hr className="mt-1 mb-2" />
                            </div>
                            <div className="col-6 px-3 mb-3">
                                <label className="filter-label"><small>Company</small></label>
                                <div className="d-flex align-items-start border px-2 py-2 rounded bg-light" style={{ minHeight: '38px' }}>
                                    <span style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{companyLabel || companyId}</span>
                                </div>
                            </div>
                            <div className="col-6 px-3 mb-3">
                                <label className="filter-label"><small>Associate Company</small></label>
                                <div className="d-flex align-items-start border px-2 py-2 rounded bg-light" style={{ minHeight: '38px' }}>
                                    <span style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{associateCompanyLabel || associateCompanyId}</span>
                                </div>
                            </div>
                            <div className="col-6 px-3 mb-3">
                                <label className="filter-label"><small>Location</small></label>
                                <div className="d-flex align-items-start border px-2 py-2 rounded bg-light" style={{ minHeight: '38px' }}>
                                    <span style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{locationLabel || locationId}</span>
                                </div>
                            </div>
                            <div className="col-6 px-3 mb-3">
                                <label className="filter-label"><small>From Month</small></label>
                                <div className="d-flex align-items-start border px-2 py-2 rounded bg-light" style={{ minHeight: '38px' }}>
                                    <span>{fromMonth} {fromYear}</span>
                                </div>
                            </div>
                            {isVendor && (
                                <>
                                    <div className="col-6 px-3 mb-3">
                                        <label className="filter-label"><small>Vendor Category</small></label>
                                        <div className="d-flex align-items-start border px-2 py-2 rounded bg-light" style={{ minHeight: '38px' }}>
                                            <span style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{vendorCategoryLabel || vendorCategoryId}</span>
                                        </div>
                                    </div>
                                    <div className="col-6 px-3 mb-3">
                                        <label className="filter-label"><small>Vendor</small></label>
                                        <div className="d-flex align-items-start border px-2 py-2 rounded bg-light" style={{ minHeight: '38px' }}>
                                            <span style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{vendorLabel || vendorRegistrationId || '—'}</span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Destination (To) — selectable */}
                        <div className="row">
                            <div className="col-12 px-3 mb-1">
                                <small className="text-muted fw-semibold text-uppercase" style={{ letterSpacing: '0.05em' }}>Destination</small>
                                <hr className="mt-1 mb-2" />
                            </div>
                            <div className="col-6 px-3 mb-3">
                                <label className="filter-label"><small>Company</small></label>
                                <Select
                                    placeholder="Select Company"
                                    options={(parentCompanies || []).map((x: any) => ({ value: x.id, label: x.name }))}
                                    value={destCompany}
                                    onChange={setDestCompany}
                                    isClearable
                                    className="select-control"
                                />
                            </div>
                            <div className="col-6 px-3 mb-3">
                                <label className="filter-label"><small>Associate Company</small></label>
                                <Select
                                    placeholder="Select Associate Company"
                                    options={(assocCompanies || []).map((x: any) => ({ value: x.id, label: x.name }))}
                                    value={destAssocCompany}
                                    onChange={setDestAssocCompany}
                                    isDisabled={!destCompany}
                                    isClearable
                                    className="select-control"
                                />
                            </div>
                            <div className="col-6 px-3 mb-3">
                                <label className="filter-label"><small>Location</small></label>
                                <Select
                                    placeholder="Select Location"
                                    options={(locations || []).map((x: any) => ({ value: x.location_id, label: x.location.name }))}
                                    value={destLocation}
                                    onChange={setDestLocation}
                                    isDisabled={!destAssocCompany}
                                    isClearable
                                    className="select-control"
                                />
                            </div>
                            {isVendor ? (
                                <div className="col-6 px-3 mb-3">
                                    <label className="filter-label"><small>Vendor Category</small></label>
                                    <Select
                                        placeholder="Select Vendor Category"
                                        options={vendorCategoryOptions}
                                        value={destVendorCategory}
                                        onChange={setDestVendorCategory}
                                        isClearable
                                        className="select-control"
                                    />
                                </div>
                            ) : (
                                <div className="col-6 px-3 mb-3">
                                    <small className="text-muted d-block mt-4 pt-1">
                                        Leave blank to replicate within the same company &amp; location.
                                    </small>
                                </div>
                            )}
                            {isVendor && (
                                <>
                                    <div className="col-6 px-3 mb-3">
                                        <label className="filter-label"><small>Vendor</small></label>
                                        <Select
                                            placeholder="Select Vendor"
                                            options={vendorRegistrationOptions}
                                            value={destVendorRegistration}
                                            onChange={setDestVendorRegistration}
                                            isDisabled={!destVendorCategory}
                                            isClearable
                                            className="select-control"
                                        />
                                    </div>
                                    <div className="col-6 px-3 mb-3">
                                        <small className="text-muted d-block mt-4 pt-1">
                                            Leave blank to replicate within the same company &amp; location.
                                        </small>
                                    </div>
                                </>
                            )}
                            {destinationIncomplete && (
                                <div className="col-12 px-3">
                                    <small className="text-danger">
                                        Please select all destination fields{isVendor ? ' (Company, Associate Company, Location, Vendor Category and Vendor)' : ' (Company, Associate Company and Location)'}, or leave all of them blank to replicate within the same company &amp; location.
                                    </small>
                                </div>
                            )}
                        </div>

                        {/* To Months selector */}
                        <div className="row mt-2">
                            <div className="col-12 px-3 mb-3">
                                <label className="filter-label"><small>To Month(s) <span className="text-danger">*</span></small></label>
                                <div className="input-group month-picker">
                                    <DatePicker
                                        multiple={true}
                                        onlyMonthPicker={true}
                                        editable={false}
                                        minDate={toMonthsMinDate}
                                        maxDate={DRAWER_MAX_DATE}
                                        format="MMM YYYY"
                                        value={toMonths}
                                        onChange={(dates: any) => setToMonths(Array.isArray(dates) ? dates : (dates ? [dates] : []))}
                                        placeholder="Select target month(s)..."
                                    >
                                        {toMonths.length > 0 && (
                                            <Button variant="link" className="mb-2" onClick={() => setToMonths([])}>Clear</Button>
                                        )}
                                    </DatePicker>
                                </div>
                            </div>
                            <div className="col-12 px-3">
                                <small className="text-muted">
                                    After replication, the audit type will remain unchanged and the audit start date will be updated to the respective month.
                                </small>
                            </div>
                        </div>

                        {/* Activity count */}
                        <div className="row mt-1">
                            <div className="col-12 px-3 mb-3">
                                <label className="filter-label"><small>Selected Activities</small></label>
                                <div className="d-flex align-items-center border px-2 rounded" style={{ height: '38px' }}>
                                    {destinationIncomplete
                                        ? <span className="text-muted">Complete the destination fields to calculate</span>
                                        : (isDryRunning
                                            ? <span className="text-muted">Calculating...</span>
                                            : <span><strong>{displayCount}</strong> / {displayTotal}</span>)
                                    }
                                </div>
                            </div>
                        </div>

                        {/* Per-month dry-run preview */}
                        {!isDryRunning && dryRunResult && dryRunResult.to_months && (
                            <div className="row mt-1">
                                <div className="col-12 px-3">
                                    {dryRunResult.to_months.map((m: any) => (
                                        <div
                                            key={`${m.month}-${m.year}`}
                                            className={`d-flex flex-column border rounded px-3 py-2 mb-2 ${m.blocked ? 'border-danger bg-light' : ''}`}
                                        >
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="fw-semibold">{m.month} {m.year}</span>
                                                {m.blocked
                                                    ? <span className="badge bg-danger">Blocked</span>
                                                    : <span className="text-success">+{m.new_count} will be created</span>
                                                }
                                            </div>
                                            {m.blocked && m.blocked_reason && (
                                                <small className="text-danger mt-1">{m.blocked_reason}</small>
                                            )}
                                            {!m.blocked && m.conflict_count > 0 && (
                                                <small className="text-warning mt-1">
                                                    {m.conflict_count} {m.conflict_count === 1 ? 'activity' : 'activities'} already exist in {m.month} {m.year} and will be skipped.
                                                </small>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <Button variant="outline-secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={toMonths.length === 0 || isSubmitting || isDryRunning || allMonthsBlocked || destinationIncomplete}
                    title={
                        destinationIncomplete
                            ? 'Complete all destination fields, or leave them all blank'
                            : (allMonthsBlocked ? 'All selected months are blocked and cannot be replicated into' : '')
                    }
                >
                    {isSubmitting ? 'Replicating...' : 'Replicate'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default AuditReplicationDrawer;
